import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  DatePicker,
  Select,
  Button,
  Table,
  Typography,
  message,
  Skeleton,
  Spin,
  Row,
  Col,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/fr";
import "dayjs/locale/en";
import type { PurchaseOrder, PurchaseOrderItem } from "../services/purchaseOrderService";
import * as supplierApi from "../services/supplierApi";
import { getRetailers, Retailer } from "../services/retailerApi";
import { useAppSelector } from "../store";
import { getSupplierPurchaseOrders } from "../services/reportingApi";
import {
  DollarOutlined,
  BarChartOutlined,
  TruckOutlined,
} from "@ant-design/icons";
import { Tabs, Tag, Steps } from "antd";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Traductions locales (FR/EN) uniquement pour cette page
const I18N = {
  fr: {
    hello: "Bonjour",
    titleMain: "Fournisseurs - Rapport de ventes",
    selectionType: "Type de sélection:",
    supplier: "Fournisseur",
    brand: "Marque",
    selectSupplierLabel: "Sélectionner un fournisseur",
    chooseSupplier: "Choisir un fournisseur",
    selectBrandsLabel: "Sélectionner une ou plusieurs marques",
    chooseBrands: "Choisir des marques",
    period: "Période",
    location: "Emplacement",
    chooseLocation: "Choisir un emplacement",
    loadReport: "Charger le rapport",
    unitsSoldPeriod: "Unités vendues (période)",
    ordersPeriod: "Commandes (période)",
    totalValuePeriod: "Valeur totale (période)",
    retailersPeriod: "Retailers actifs (période)",
    distinctProducts: "Produits distincts",
    salesReport: "Rapport des ventes",
    exportCsv: "Exporter CSV",
    date: "Date",
    orderDate: "Date commande",
    unitsSold: "Unités vendues",
    orders: "Commandes",
    totalValue: "Valeur totale",
    totals: "Totaux",
    productDetailsPeriod: "Détails des produits (période)",
    product: "Produit",
    barcode: "Code-barres",
    quantity: "Quantité",
    value: "Valeur",
    noProductsPeriod: "Aucun produit sur la période",
    errInit: "Impossible de charger les données initiales",
    errNoReportingAccess: "Vous n'avez pas l'accès reporting",
    warnNoSupplierScope: "Aucun fournisseur assigné à votre périmètre",
    warnSelectSupplier: "Veuillez sélectionner un fournisseur",
    warnNoBrandScope: "Aucune marque autorisée dans votre périmètre",
    warnSelectAtLeastOneBrand: "Veuillez sélectionner au moins une marque",
    warnNoLocation: "Aucun emplacement disponible",
    errLoadReport: "Impossible de charger le rapport",
    periodFrom: "Du",
    periodTo: "au",
    csvDaysHeaders: ["Date commande", "Retailer", "Unités vendues", "Valeur totale"],
    csvProductsHeaders: ["Produit", "Code-barres", "Quantité", "Valeur"],
    csvDaysFilename: "ventes_par_jour.csv",
    csvProductsFilename: "ventes_par_produit.csv",
    orderTracking: "Suivi des commandes",
    financials: "Finances & Rétrocessions",
    salesOverview: "Aperçu des ventes",
    retailer: "Distributeur",
    deliveryStatus: "Statut livraison",
    paymentStatus: "Statut paiement",
    retrocession: "Rétrocession",
    amount: "Montant",
    pending: "En attente",
    waiting: "En attente",
    paid: "Payé",
    received: "Reçu",
    partial: "Partiel",
    performanceByBrand: "Performance par Marque",
    performanceByRetailer: "Performance par Retailer",
    retroStatus: "Statut Rétro",
    orderStep: "Commande",
    deliveryStep: "Livraison",
    paymentStep: "Paiement",
    retroStep: "Rétrocession",
    deliveryDate: "Date livraison",
    waitingPayment: "En attente",
    paymentPlanned: "Paiement prévu le",
    paymentLate: "Paiement en retard",
    ref: "Réf",
    sent: "Envoyé",
    draft: "Brouillon",
    cancelled: "Annulé",
    all: "Tous",
  },
  en: {
    hello: "Hello",
    titleMain: "Suppliers - Sales report",
    selectionType: "Selection type:",
    supplier: "Supplier",
    brand: "Brand",
    selectSupplierLabel: "Select a supplier",
    chooseSupplier: "Choose a supplier",
    selectBrandsLabel: "Select one or more brands",
    chooseBrands: "Choose brands",
    period: "Period",
    location: "Location",
    chooseLocation: "Choose a location",
    loadReport: "Load report",
    unitsSoldPeriod: "Units sold (period)",
    ordersPeriod: "Orders (period)",
    totalValuePeriod: "Total value (period)",
    retailersPeriod: "Active retailers (period)",
    distinctProducts: "Distinct products",
    salesReport: "Sales report",
    exportCsv: "Export CSV",
    date: "Date",
    orderDate: "Order date",
    unitsSold: "Units sold",
    orders: "Orders",
    totalValue: "Total value",
    totals: "Totals",
    productDetailsPeriod: "Product details (period)",
    product: "Product",
    barcode: "Barcode",
    quantity: "Quantity",
    value: "Value",
    noProductsPeriod: "No products in the period",
    errInit: "Failed to load initial data",
    errNoReportingAccess: "You do not have reporting access",
    warnNoSupplierScope: "No supplier assigned to your scope",
    warnSelectSupplier: "Please select a supplier",
    warnNoBrandScope: "No brand allowed in your scope",
    warnSelectAtLeastOneBrand: "Please select at least one brand",
    warnNoLocation: "No location available",
    errLoadReport: "Failed to load report",
    periodFrom: "From",
    periodTo: "to",
    csvDaysHeaders: ["Order date", "Retailer", "Units sold", "Total value"],
    csvProductsHeaders: ["Product", "Barcode", "Quantity", "Value"],
    csvDaysFilename: "sales_by_day.csv",
    csvProductsFilename: "sales_by_product.csv",
    orderTracking: "Order Tracking",
    financials: "Financials & Retrocessions",
    salesOverview: "Sales Overview",
    retailer: "Retailer",
    deliveryStatus: "Delivery Status",
    paymentStatus: "Payment Status",
    retrocession: "Retrocession",
    amount: "Amount",
    pending: "Pending",
    waiting: "Pending",
    paid: "Paid",
    received: "Received",
    partial: "Partial",
    performanceByBrand: "Performance by Brand",
    performanceByRetailer: "Performance by Retailer",
    retroStatus: "Retro Status",
    orderStep: "Order",
    deliveryStep: "Delivery",
    paymentStep: "Payment",
    retroStep: "Retrocession",
    deliveryDate: "Delivery date",
    waitingPayment: "Pending",
    paymentPlanned: "Payment expected on",
    paymentLate: "Payment overdue",
    ref: "Ref",
    sent: "Sent",
    draft: "Draft",
    cancelled: "Cancelled",
    all: "All",
  },
  kr: {
    hello: "안녕하세요",
    titleMain: "공급업체 - 판매 보고서",
    selectionType: "선택 유형:",
    supplier: "공급업체",
    brand: "브랜드",
    selectSupplierLabel: "공급업체 선택",
    chooseSupplier: "공급업체 선택",
    selectBrandsLabel: "하나 이상의 브랜드 선택",
    chooseBrands: "브랜드 선택",
    period: "기간",
    location: "위치",
    chooseLocation: "위치 선택",
    loadReport: "보고서 불러오기",
    unitsSoldPeriod: "판매 수량 (기간)",
    ordersPeriod: "주문 수 (기간)",
    totalValuePeriod: "총 가치 (기간)",
    retailersPeriod: "활성 리테일러 수 (기간)",
    distinctProducts: "개별 제품 수",
    salesReport: "판매 보고서",
    exportCsv: "CSV 내보내기",
    date: "날짜",
    orderDate: "주문일",
    unitsSold: "판매 수량",
    orders: "주문 수",
    totalValue: "총 가치",
    totals: "합계",
    productDetailsPeriod: "제품 상세 정보 (기간)",
    product: "제품",
    barcode: "바코드",
    quantity: "수량",
    value: "가치",
    noProductsPeriod: "해당 기간에 제품이 없습니다",
    errInit: "초기 데이터를 불러오지 못했습니다",
    errNoReportingAccess: "보고서 접근 권한이 없습니다",
    warnNoSupplierScope: "지정된 공급업체 범위가 없습니다",
    warnSelectSupplier: "공급업체를 선택해주세요",
    warnNoBrandScope: "허용된 브랜드 범위가 없습니다",
    warnSelectAtLeastOneBrand: "최소 하나 이상의 브랜드를 선택해주세요",
    warnNoLocation: "사용 가능한 위치가 없습니다",
    errLoadReport: "보고서를 불러오지 못했습니다",
    periodFrom: "시작",
    periodTo: "종료",
    csvDaysHeaders: ["주문일", "리테일러", "판매 수량", "총 가치"],
    csvProductsHeaders: ["제품", "바코드", "수량", "가치"],
    csvDaysFilename: "sales_by_day.csv",
    csvProductsFilename: "sales_by_product.csv",
    orderTracking: "주문 추적",
    financials: "재무 및 리베이트",
    salesOverview: "판매 개요",
    retailer: "소매업체",
    deliveryStatus: "배송 상태",
    paymentStatus: "결제 상태",
    retrocession: "리베이트",
    amount: "금액",
    pending: "대기 중",
    waiting: "대기 중",
    paid: "결제됨",
    received: "수령됨",
    partial: "부분 결제",
    performanceByBrand: "브랜드별 실적",
    performanceByRetailer: "리테일러별 실적",
    retroStatus: "리베이트 상태",
    orderStep: "주문",
    deliveryStep: "배송",
    paymentStep: "결제",
    retroStep: "리베이트",
    deliveryDate: "배송일",
    waitingPayment: "대기 중",
    paymentPlanned: "결제 예정일",
    paymentLate: "결제 지연",
    ref: "참조",
    sent: "발송됨",
    draft: "초안",
    cancelled: "취소됨",
    all: "전체",
    waiting_payment: "결제 대기",
    waiting_retrocession: "리베이트 대기",
  },
} as const;

// Localisation française
dayjs.locale("fr");

interface ReportRow {
  key: string;
  date: string; // YYYY-MM-DD
  retailerName: string;
  retailerId?: string;
  reference?: string;
  unitsSold: number;
  totalValue: number;
}

const Fournisseurs: React.FC = () => {
  // Filtres
  const [suppliers, setSuppliers] = useState<supplierApi.Supplier[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<
    string | undefined
  >();

  const [retailers, setRetailers] = useState<Retailer[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<
    string | undefined
  >();

  // Dates par défaut: année en cours
  const getInitialDateRange = (): [Dayjs, Dayjs] => {
    const today = dayjs();
    const start = today.startOf("year").startOf("day");
    const end = today.endOf("day");
    return [start, end];
  };
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(
    getInitialDateRange()
  );

  // Données
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loadingPOs, setLoadingPOs] = useState(false);

  // Langue (FR par défaut)
  const [lang, setLang] = useState<"fr" | "en" | "kr">("fr");
  const t = I18N[lang];

  // Détails produits pour toute la période
  const [detailsAll, setDetailsAll] = useState<
    Array<{
      productId: string;
      title: string;
      quantity: number;
      barcode?: string;
      value: number;
    }>
  >([]);
  const [currency, setCurrency] = useState<string>("EUR");
  const [totalsDays, setTotalsDays] = useState<{
    units: number;
    orders: number;
    value: number;
    retailers: number;
  }>({ units: 0, orders: 0, value: 0, retailers: 0 });
  const [totalsProducts, setTotalsProducts] = useState<{
    distinct: number;
    qty: number;
    value: number;
  }>({ distinct: 0, qty: 0, value: 0 });

  const [retailerStats, setRetailerStats] = useState<any[]>([]);
  const [performanceDimension, setPerformanceDimension] = useState<"brand" | "retailer">("brand");
  const reportRequestRef = useRef(0);

  const normalizeStr = (s: string) =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const getRetrocessionBase = (po: PurchaseOrder) => {
    const supplierTotals = (po.items || []).reduce((sum, item) => {
      if (Number.isFinite(item.supplierTotalPrice)) return sum + (item.supplierTotalPrice || 0);
      if (Number.isFinite(item.supplierUnitPrice)) {
        return sum + (item.supplierUnitPrice || 0) * (item.quantity || 0);
      }
      return sum;
    }, 0);
    return supplierTotals > 0 ? supplierTotals : 0;
  };

  const getSupplierItemValue = (item: PurchaseOrderItem) => {
    if (Number.isFinite(item.supplierTotalPrice)) return item.supplierTotalPrice || 0;
    if (Number.isFinite(item.supplierUnitPrice)) {
      return (item.supplierUnitPrice || 0) * (item.quantity || 0);
    }
    return 0;
  };

  const getReportingItemValue = (item: PurchaseOrderItem) => {
    if (Number.isFinite(item.totalPrice)) return item.totalPrice || 0;
    if (Number.isFinite(item.unitPrice)) {
      return (item.unitPrice || 0) * (item.quantity || 0);
    }
    return getSupplierItemValue(item);
  };

  const getRetrocessionAmount = (po: PurchaseOrder) => {
    const base = getRetrocessionBase(po);
    const rate = po.retrocessionRate || 0;
    if (rate > 0) return (base * rate) / 100;
    return po.retrocessionAmount || 0;
  };

  const getSupplierOrderTotal = (po: PurchaseOrder) => getRetrocessionBase(po);
  const getDeliveryDate = (po: PurchaseOrder): string | null => {
    const d = po.deliveredAt || po.expectedDeliveryDate || po.deliveryDate || null;
    if (!d) return null;
    return dayjs(d).isValid() ? dayjs(d).format("DD/MM/YYYY") : null;
  };

  const getPaymentTrackingState = (po: PurchaseOrder) => {
    const paid = po.paymentStatus === "paid" || !!po.paymentDate;
    const dueRaw = po.paymentDueDate || po.incomingPayment?.invoice?.dueAt || null;
    const dueDate = dueRaw && dayjs(dueRaw).isValid() ? dayjs(dueRaw) : null;
    if (paid) {
      const paidAt = po.paymentDate && dayjs(po.paymentDate).isValid() ? dayjs(po.paymentDate).format("DD/MM/YYYY") : t.paid;
      return { color: "#2563eb", label: `${t.paid}${paidAt !== t.paid ? ` • ${paidAt}` : ""}` };
    }
    if (dueDate && dayjs().isAfter(dueDate, "day")) {
      return { color: "#ca8a04", label: `${t.paymentLate} • ${dueDate.format("DD/MM/YYYY")}` };
    }
    if (dueDate) {
      return { color: "#6b7280", label: `${t.paymentPlanned} ${dueDate.format("DD/MM/YYYY")}` };
    }
    return { color: "#6b7280", label: t.waitingPayment };
  };

  const formatCurrency = (v: number) => {
    try {
      let locale = "fr-FR";
      if (lang === "en") locale = "en-US";
      if (lang === "kr") locale = "ko-KR";
      
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
      }).format(v || 0);
    } catch {
      return `${(v || 0).toFixed(2)} ${currency}`;
    }
  };

  // Utilisateur courant et périmètre de reporting
  const currentUser = useAppSelector((s) => s.user.currentUser);
  const isAdmin = !!currentUser?.accesses?.includes("admin");
  const hasReportingAccess = !!currentUser?.accesses?.includes("reporting") || isAdmin;
  const scopeSupplierId = currentUser?.reportingSupplierId || null;
  const scopeBrands = currentUser?.reportingBrands || [];
  const isSupplierDept = Boolean(currentUser?.departmentFlags?.isSupplier);

  useEffect(() => {
    const init = async () => {
      try {
        setLoadingInitial(true);

        // Retailers active
        const allRetailers = await getRetailers();
        const activeRetailers = allRetailers.filter((r: Retailer) => r.status === 'active' || r.crm?.onboardingStage === 'launched');
        setRetailers(activeRetailers);
        if (!selectedRetailerId) {
          setSelectedRetailerId("all");
        }

        if (hasReportingAccess || isAdmin) {
          if (isSupplierDept) {
            if (scopeSupplierId && !selectedSupplierId) {
              setSelectedSupplierId(scopeSupplierId);
            }
          } else {
            const supplierList = await supplierApi.getAllSuppliers();
            setSuppliers(supplierList);
            if (!selectedSupplierId) {
              setSelectedSupplierId("all");
            }
          }
        }
      } catch (err) {
        console.error(err);
        message.error(t.errInit);
      } finally {
        setLoadingInitial(false);
      }
    };
    init();
  }, [hasReportingAccess, scopeSupplierId, isSupplierDept, lang, isAdmin]);

  // Texte d'affichage de la période (ex: Du 18 juillet au 25 août)
  const periodDisplay =
    dateRange && dateRange[0] && dateRange[1]
      ? `${t.periodFrom} ${dateRange[0].locale(lang).format("D MMMM")} ${
          t.periodTo
        } ${dateRange[1].locale(lang).format("D MMMM")}`
      : "";

  const handleLoadReport = async () => {
    const requestId = ++reportRequestRef.current;
    try {
      if (!hasReportingAccess) {
        message.error(t.errNoReportingAccess);
        return;
      }
      // Déterminer le mode effectif selon le département/périmètre
      const effectiveSupplierId = isSupplierDept
        ? scopeSupplierId || undefined
        : selectedSupplierId;

      // Validations adaptées
      if (isSupplierDept && !effectiveSupplierId) {
        message.warning(
          isSupplierDept ? t.warnNoSupplierScope : t.warnSelectSupplier
        );
        return;
      }

      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");

      setLoading(true);
      setLoadingPOs(true);

      const allowAllSuppliers = !isSupplierDept && effectiveSupplierId === "all";
      const retailerFilter = selectedRetailerId && selectedRetailerId !== "all" ? selectedRetailerId : null;
      const supplierFilter = allowAllSuppliers ? null : effectiveSupplierId || null;

      const allPurchaseOrders = await getSupplierPurchaseOrders({
        startDate,
        endDate,
        retailerId: retailerFilter,
        supplierId: supplierFilter,
        syncPennylane: true,
      });
      if (requestId !== reportRequestRef.current) return;

      const selectedSupplier = suppliers.find(
        (supplier) => (supplier.id || supplier._id) === supplierFilter
      );
      const scopedBrands = new Set(scopeBrands.map((brand) => normalizeStr(brand)));
      const selectedSupplierNames = new Set(
        [
          selectedSupplier?.name,
          ...(selectedSupplier?.vendorBrands || []),
        ]
          .filter(Boolean)
          .map((name) => normalizeStr(String(name)))
      );

      const getScopedItems = (po: PurchaseOrder) => {
        const items = po.items || [];
        if (scopedBrands.size > 0) {
          return items.filter((item) =>
            scopedBrands.has(normalizeStr(item.vendor || po.supplier || ""))
          );
        }
        if (supplierFilter && selectedSupplierNames.size > 0) {
          return items.filter((item) =>
            selectedSupplierNames.has(normalizeStr(item.vendor || po.supplier || ""))
          );
        }
        return items;
      };

      const filteredPurchaseOrders = allPurchaseOrders
        .map((po: PurchaseOrder) => ({
          ...po,
          items: getScopedItems(po),
        }))
        .filter((po: PurchaseOrder) => (po.items || []).length > 0);

      const detailsAllAccumulator: Map<
        string,
        { title: string; quantity: number; barcode?: string; value: number }
      > = new Map();
      const retailerIds = new Set<string>();
      const reportRows: ReportRow[] = [];
      const retailerPerformanceMap = new Map<
        string,
        { name: string; units: number; orders: number; value: number; orderIds: Set<string> }
      >();
      let currencyUsed = "EUR";

      for (const po of filteredPurchaseOrders) {
        const orderDate = dayjs(po.creationDate || po.orderDate || po.updatedAt).format("YYYY-MM-DD");
        const items = po.items || [];
        let unitsInThisOrder = 0;
        let valueInThisOrder = 0;
        items.forEach((item: PurchaseOrderItem) => {
          unitsInThisOrder += item.quantity || 0;
          valueInThisOrder += getReportingItemValue(item);
          const detailKey = item.productId || `${item.vendor || ""}|${item.productName}`;
          const prev = detailsAllAccumulator.get(detailKey) || {
            title: item.productName || t.product,
            quantity: 0,
            value: 0,
            barcode: item.barcode,
          };
          prev.quantity += item.quantity || 0;
          prev.value += getReportingItemValue(item);
          if (!prev.barcode && item.barcode) prev.barcode = item.barcode;
          detailsAllAccumulator.set(detailKey, prev);
        });

        if (unitsInThisOrder > 0) {
          const retailerKey = String(po.retailerId || po.retailerName || "");
          if (retailerKey) retailerIds.add(retailerKey);
          const retailerStatsRow = retailerPerformanceMap.get(retailerKey) || {
            name: po.retailerName || "Retailer non renseigné",
            units: 0,
            orders: 0,
            value: 0,
            orderIds: new Set<string>(),
          };
          retailerStatsRow.units += unitsInThisOrder;
          retailerStatsRow.value += valueInThisOrder;
          retailerStatsRow.orderIds.add(String(po._id || po.reference || orderDate));
          retailerStatsRow.orders = retailerStatsRow.orderIds.size;
          retailerPerformanceMap.set(retailerKey, retailerStatsRow);
          reportRows.push({
            key: String(po._id || po.reference || `${orderDate}-${reportRows.length}`),
            date: orderDate,
            retailerName: po.retailerName || "—",
            retailerId: po.retailerId,
            reference: po.reference,
            unitsSold: unitsInThisOrder,
            totalValue: valueInThisOrder,
          });
        }
      }

      // Calculate Retailer/Brand Stats
      const brandMap: Map<string, { name: string, units: number, orders: number, value: number, orderIds: Set<string> }> = new Map();
      filteredPurchaseOrders.forEach((po: PurchaseOrder) => {
        const orderId = po._id || po.reference || "";
        (po.items || []).forEach((item: PurchaseOrderItem) => {
          const brandName = item.vendor || po.supplier || "Unknown";
          const stats = brandMap.get(brandName) || { name: brandName, units: 0, orders: 0, value: 0, orderIds: new Set<string>() };
          stats.units += item.quantity || 0;
          stats.value += getReportingItemValue(item);
          if (orderId) stats.orderIds.add(String(orderId));
          stats.orders = stats.orderIds.size;
          brandMap.set(brandName, stats);
        });
      });
      const newRows = reportRows.sort((a, b) => b.date.localeCompare(a.date));

      setRows(newRows);
      
      // Construire les détails sur toute la période
      const builtDetailsAll = Array.from(detailsAllAccumulator.entries())
        .map(([pid, info]) => ({
          productId: pid,
          title: info.title,
          quantity: info.quantity,
          barcode: info.barcode,
          value: info.value,
        }))
        .sort((a, b) => b.quantity - a.quantity);
      setDetailsAll(builtDetailsAll);
      setCurrency(currencyUsed);

      // Totaux globaux
      const totalsU = newRows.reduce((s, r) => s + r.unitsSold, 0);
      const totalsO = newRows.length;
      const totalsV = newRows.reduce((s, r) => s + r.totalValue, 0);
      setTotalsDays({
        units: totalsU,
        orders: totalsO,
        value: totalsV,
        retailers: retailerIds.size,
      });
      
      const totQty = builtDetailsAll.reduce((s, d) => s + d.quantity, 0);
      const totVal = builtDetailsAll.reduce((s, d) => s + d.value, 0);
      setTotalsProducts({
        distinct: builtDetailsAll.length,
        qty: totQty,
        value: totVal,
      });

      const hasSupplierScope = Boolean(supplierFilter || scopeBrands.length > 0);
      setPerformanceDimension(hasSupplierScope ? "retailer" : "brand");
      setRetailerStats(
        (hasSupplierScope
          ? Array.from(retailerPerformanceMap.values())
          : Array.from(brandMap.values())
        ).sort((a, b) => b.value - a.value)
      );

      setPurchaseOrders(filteredPurchaseOrders);
      setLoadingPOs(false);
    } catch (error) {
      console.error(
        "Erreur lors du chargement du rapport fournisseurs:",
        error
      );
      message.error(t.errLoadReport);
    } finally {
      if (requestId === reportRequestRef.current) {
        setLoading(false);
        setLoadingPOs(false);
      }
    }
  };

  // Chaque changement de filtre régénère réellement les données. Le délai court
  // évite de lancer plusieurs appels lorsqu'une plage de dates est saisie.
  useEffect(() => {
    if (!hasReportingAccess || loadingInitial) return;
    const effectiveSupplierId = isSupplierDept ? scopeSupplierId : selectedSupplierId;
    if (isSupplierDept && !effectiveSupplierId) return;
    if (!selectedRetailerId && retailers.length > 0) return;

    const timer = window.setTimeout(() => {
      handleLoadReport();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [
    hasReportingAccess,
    loadingInitial,
    selectedSupplierId,
    selectedRetailerId,
    dateRange[0].valueOf(),
    dateRange[1].valueOf(),
    isSupplierDept,
    scopeSupplierId,
    scopeBrands.join("|"),
    retailers.length,
  ]);

  return (
    <div className="reporting-screen">
      <div className="reporting-hero">
        <div className="reporting-hero-content">
          <div>
            <div className="reporting-eyebrow">Cockpit Analytics</div>
            <Title level={1} className="reporting-title">
              {isSupplierDept && currentUser?.companyName
                ? `${t.hello}, ${currentUser.companyName}`
                : t.titleMain}
            </Title>
            <div className="reporting-subtitle">
              {lang === "fr" && "Pilotez votre performance en temps réel. Suivez vos volumes de ventes, vos flux logistiques et vos rétrocessions avec une précision chirurgicale."}
              {lang === "en" && "Drive your performance in real-time. Track sales volumes, logistics flows, and retrocessions with surgical precision."}
              {lang === "kr" && "실시간으로 실적을 관리하세요. 판매량, 물류 흐름 및 소급분을 정밀하게 추적합니다."}
            </div>
            
            <div className="reporting-hero-meta">
              <div className="reporting-meta-badge">
                <span className="reporting-meta-label">{t.period}</span>
                <span className="reporting-meta-value">{periodDisplay || "—"}</span>
              </div>
              <div className="reporting-meta-badge">
                <span className="reporting-meta-label">{t.retailer}</span>
                <span className="reporting-meta-value">
                  {selectedRetailerId
                    ? retailers.find((r) => (r.id || r._id) === selectedRetailerId)?.name || "—"
                    : "—"}
                </span>
              </div>
            </div>
          </div>
          <div className="reporting-hero-actions gap-4">
            <Button
              type="primary"
              size="large"
              icon={<DownloadOutlined />}
              onClick={() => exportDaysCsv(rows, lang)}
              disabled={rows.length === 0}
              className="premium-download-btn !bg-white !text-[#0f172a] border-none shadow-sm hover:shadow-md transition-all"
            >
              {t.exportCsv}
            </Button>
            <div className="premium-lang-selector mt-2 bg-white/20 p-1 rounded-full flex items-center gap-1 backdrop-blur-sm">
              {(["fr", "en", "kr"] as const).map((l) => (
                <Button
                  key={l}
                  size="small"
                  onClick={() => setLang(l)}
                  className={`w-8 h-8 flex items-center justify-center text-[10px] font-bold border-none rounded-full transition-all ${
                    lang === l 
                      ? "!bg-white !text-[#0f172a] shadow-sm" 
                      : "!bg-transparent !text-white hover:!bg-white/30"
                  }`}
                >
                  {l.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="reporting-kpi-grid">
        <Card className="reporting-kpi-card">
          <div className="reporting-kpi-label">{t.totalValuePeriod}</div>
          {loading || loadingInitial ? <Skeleton.Input active size="small" /> : (
            <Title level={2} className="reporting-kpi-value">{formatCurrency(totalsDays.value)}</Title>
          )}
        </Card>
        <Card className="reporting-kpi-card">
          <div className="reporting-kpi-label">{t.ordersPeriod}</div>
          {loading || loadingInitial ? <Skeleton.Input active size="small" /> : (
            <Title level={2} className="reporting-kpi-value">{totalsDays.orders}</Title>
          )}
        </Card>
        <Card className="reporting-kpi-card">
          <div className="reporting-kpi-label">{t.retailersPeriod}</div>
          {loading || loadingInitial ? <Skeleton.Input active size="small" /> : (
            <Title level={2} className="reporting-kpi-value">{totalsDays.retailers}</Title>
          )}
        </Card>
        <Card className="reporting-kpi-card">
          <div className="reporting-kpi-label">{t.unitsSoldPeriod}</div>
          {loading || loadingInitial ? <Skeleton.Input active size="small" /> : (
            <Title level={2} className="reporting-kpi-value">{totalsDays.units}</Title>
          )}
        </Card>
      </div>

      <div className="reporting-filter-bar items-end gap-6">
        {!isSupplierDept && (
          <div className="filter-group">
            <Text strong className="filter-label">{t.supplier}</Text>
            <Select
              size="large"
              allowClear
              showSearch
              style={{ minWidth: 260, height: 48 }}
              className="premium-select"
              placeholder={t.selectSupplierLabel}
              options={[
                { value: "all", label: t.all },
                ...suppliers.map((s) => ({
                  value: s.id || s._id,
                  label: s.name,
                })),
              ]}
              value={selectedSupplierId}
              onChange={(v) => setSelectedSupplierId(v || "all")}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </div>
        )}

        <div className="filter-group">
          <Text strong className="filter-label">{t.period}</Text>
          <RangePicker
            size="large"
            value={dateRange}
            onChange={(vals) => vals && setDateRange(vals as [Dayjs, Dayjs])}
            disabledDate={(current) => current && current > dayjs().endOf("day")}
            allowClear={false}
            style={{ borderRadius: '12px', height: 48, minWidth: 280 }}
          />
        </div>

        <div className="filter-group">
          <Text strong className="filter-label">{t.retailer}</Text>
          <Select
            size="large"
            allowClear
            showSearch
            style={{ minWidth: 280, height: 48 }}
            className="premium-select"
            placeholder={t.retailer}
            options={[
              { value: "all", label: t.all },
              ...retailers.map((r) => ({
                value: r.id || r._id,
                label: r.name,
              })),
            ]}
            value={selectedRetailerId}
            onChange={(v) => setSelectedRetailerId(v || "all")}
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </div>

        <Button
          type="primary"
          size="large"
          onClick={handleLoadReport}
          disabled={loadingInitial}
          className="premium-load-btn"
          style={{ height: 48, borderRadius: '12px', fontWeight: 600, padding: '0 32px' }}
        >
          {t.loadReport}
        </Button>
      </div>

      {loading || loadingInitial ? (
        <Card className="reporting-table-card">
          <div style={{ textAlign: "center", padding: 48 }}>
            <Spin />
          </div>
        </Card>
      ) : (
        <Tabs defaultActiveKey="sales" className="reporting-tabs">
          <TabPane 
            tab={<span><BarChartOutlined /> {t.salesOverview}</span>} 
            key="sales"
          >
            <div className="reporting-tab-content">
              <Row gutter={[24, 24]}>
                <Col xs={24} xl={16}>
                  <Card
                    className="premium-table-card"
                    title={
                      <div
                        className="card-header-with-badge"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <span>{t.salesReport}</span>
                        {periodDisplay && <Tag className="premium-tag">{periodDisplay}</Tag>}
                      </div>
                    }
                    extra={
                      <Button
                        icon={<DownloadOutlined />}
                        size="middle"
                        onClick={() => exportDaysCsv(rows, lang)}
                        className="premium-action-btn"
                      >
                        {t.exportCsv}
                      </Button>
                    }
                  >
                    <Table
                      size="middle"
                      dataSource={rows}
                      pagination={{ pageSize: 10, showSizeChanger: false }}
                      className="premium-table"
                      columns={[
                        {
                          title: t.orderDate,
                          dataIndex: "date",
                          key: "date",
                          render: (v: string) =>
                            <Text strong>{dayjs(v).locale(lang).format("DD MMM")}</Text>,
                        },
                        {
                          title: t.retailer,
                          dataIndex: "retailerName",
                          key: "retailerName",
                          render: (value: string) => <Text strong>{value}</Text>,
                        },
                        {
                          title: t.unitsSold,
                          dataIndex: "unitsSold",
                          key: "unitsSold",
                          align: "center",
                        },
                        {
                          title: t.totalValue,
                          dataIndex: "totalValue",
                          key: "totalValue",
                          align: "right",
                          render: (v: number) => <Text strong className="premium-amount">{formatCurrency(v)}</Text>,
                        },
                      ]}
                      rowKey="key"
                      summary={() => (
                        <Table.Summary fixed>
                          <Table.Summary.Row className="summary-row-premium">
                            <Table.Summary.Cell index={0}>
                              <Text strong>{t.totals}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} align="center">
                              <Text strong>{totalsDays.retailers} retailers</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} align="center">
                              <Text strong>{totalsDays.units}</Text>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} align="right">
                              <Text strong className="premium-amount-total">{formatCurrency(totalsDays.value)}</Text>
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        </Table.Summary>
                      )}
                    />
                  </Card>
                </Col>
                <Col xs={24} xl={8}>
                  <Card
                    title={
                      performanceDimension === "retailer"
                        ? t.performanceByRetailer
                        : t.performanceByBrand
                    }
                    className="premium-table-card h-full"
                  >
                    <Table
                      size="middle"
                      dataSource={retailerStats}
                      pagination={false}
                      className="premium-table"
                      columns={[
                        {
                          title: performanceDimension === "retailer" ? t.retailer : t.brand,
                          dataIndex: 'name',
                          key: 'name',
                          render: (text) => <Text strong>{text}</Text>
                        },
                        {
                          title: t.value,
                          dataIndex: 'value',
                          key: 'value',
                          align: 'right',
                          render: (v) => <Text className="premium-amount-small">{formatCurrency(v)}</Text>
                        },
                      ]}
                    />
                  </Card>
                </Col>
                <Col xs={24}>
                  <Card
                    className="premium-table-card"
                    title={t.productDetailsPeriod}
                    extra={
                      <Button
                        icon={<DownloadOutlined />}
                        size="middle"
                        onClick={() => exportProductsCsv(detailsAll, lang)}
                        className="premium-action-btn"
                      >
                        {t.exportCsv}
                      </Button>
                    }
                  >
                    <Table
                      size="middle"
                      dataSource={detailsAll}
                      pagination={{ pageSize: 15 }}
                      className="premium-table"
                      columns={[
                        { 
                          title: t.product, 
                          dataIndex: "title", 
                          key: "title",
                          render: (text) => <Text strong>{text}</Text>
                        },
                        {
                          title: t.barcode,
                          dataIndex: "barcode",
                          key: "barcode",
                          render: (text) => <Text type="secondary" style={{ fontFamily: 'monospace' }}>{text || '—'}</Text>
                        },
                        { 
                          title: t.quantity, 
                          dataIndex: "quantity", 
                          key: "quantity",
                          align: "center"
                        },
                        {
                          title: t.value,
                          dataIndex: "value",
                          key: "value",
                          align: "right",
                          render: (v: number) => <Text strong className="premium-amount">{formatCurrency(v)}</Text>,
                        },
                      ]}
                      rowKey="productId"
                      locale={{ emptyText: t.noProductsPeriod }}
                    />
                  </Card>
                </Col>
              </Row>
            </div>
          </TabPane>

          <TabPane 
            tab={<span><TruckOutlined /> {t.orderTracking}</span>} 
            key="tracking"
          >
            <Card className="premium-table-card">
              <Table
                loading={loadingPOs}
                dataSource={purchaseOrders}
                rowKey="_id"
                className="premium-table"
                columns={[
                  {
                    title: t.ref,
                    dataIndex: 'reference',
                    key: 'reference',
                    render: (text) => <Text strong>{text}</Text>
                  },
                  {
                    title: t.retailer,
                    dataIndex: 'retailerName',
                    key: 'retailerName',
                    render: (text) => text || '—'
                  },
                  {
                    title: t.date,
                    dataIndex: 'creationDate',
                    key: 'creationDate',
                    render: (date) => dayjs(date).format('DD/MM/YYYY')
                  },
                  {
                    title: t.deliveryStatus,
                    dataIndex: 'status',
                    key: 'status',
                    render: (status) => {
                      const colors: Record<string, string> = {
                        received: 'success',
                        sent: 'processing',
                        draft: 'default',
                        cancelled: 'error'
                      };
                      return (
                        <Tag color={colors[status] || 'default'}>
                          {String(t[status as keyof typeof t] || status).toUpperCase()}
                        </Tag>
                      );
                    }
                  },
                  {
                    title: t.deliveryDate,
                    key: 'deliveryDate',
                    render: (_value, record) => getDeliveryDate(record) || t.waiting,
                  },
                  {
                    title: t.amount,
                    dataIndex: 'totalAmount',
                    key: 'totalAmount',
                    render: (_value, record) => formatCurrency(getSupplierOrderTotal(record))
                  }
                ]}
              />
            </Card>
          </TabPane>

          <TabPane 
            tab={<span><DollarOutlined /> {t.financials}</span>} 
            key="finance"
          >
            <Card className="premium-table-card">
              {loadingPOs ? (
                <div style={{ textAlign: "center", padding: 48 }}>
                  <Spin />
                </div>
              ) : purchaseOrders.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32 }}>
                  <Text type="secondary">Aucune commande à rétrocéder sur la période.</Text>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {purchaseOrders.map((po) => {
                    const orderStatus = po.status || "draft";
                    const deliveryStarted = [
                      "sent",
                      "in_delivery",
                      "partially_received",
                      "noted_delivered",
                      "received",
                      "completed",
                    ].includes(orderStatus);
                    const deliveryDone = ["received", "completed"].includes(orderStatus);
                    const retroStatus = po.retrocessionStatus || "pending";
                    const paymentStatus = po.paymentStatus || "pending";
                    const baseAmount = getRetrocessionBase(po);
                    const poDate = dayjs(po.creationDate || po.orderDate || po.updatedAt).format("DD/MM/YYYY");
                    const deliveryDate = getDeliveryDate(po) || t.waiting;
                    const paymentReceived = paymentStatus === "paid" || !!po.paymentDate;
                    const paymentTracking = getPaymentTrackingState(po);
                    const retroSent = ["sent", "received"].includes(retroStatus);
                    const stepIndex = retroSent ? 3 : paymentReceived ? 2 : deliveryStarted ? 1 : 0;

                    return (
                      <div
                        key={String(po._id || po.reference)}
                        style={{
                          background: "#ffffff",
                          borderRadius: 16,
                          padding: 20,
                          border: "1px solid #e2e8f0",
                          boxShadow: "0 6px 18px rgba(15, 23, 42, 0.06)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 16,
                            flexWrap: "wrap",
                            marginBottom: 16,
                          }}
                        >
                          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                            <Text strong style={{ fontSize: 16, color: "#0f172a" }}>
                              {po.reference || "—"}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {po.retailerName || t.retailer} • {poDate}
                            </Text>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <Tag color={deliveryDone ? "green" : deliveryStarted ? "blue" : "default"}>
                                {deliveryDone ? t.received : deliveryStarted ? t.sent : t.draft}
                              </Tag>
                              <Tag color={retroStatus === "received" ? "green" : retroStatus === "partial" ? "gold" : "orange"}>
                                {t[retroStatus as keyof typeof t] || retroStatus}
                              </Tag>
                            </div>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, textAlign: "right" }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>{t.totalValue}</Text>
                            <Text strong style={{ fontSize: 18 }}>{formatCurrency(baseAmount)}</Text>
                          </div>
                        </div>

                        <Steps
                          current={stepIndex}
                          size="small"
                          responsive
                          items={[
                            { title: t.orderStep, description: poDate },
                            {
                              title: t.deliveryStep,
                              description: deliveryDate,
                            },
                            {
                              title: t.paymentStep,
                              description: <span style={{ color: paymentTracking.color }}>{paymentTracking.label}</span>,
                            },
                            {
                              title: t.retroStep,
                              description: t[retroStatus as keyof typeof t] || retroStatus,
                            },
                          ]}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </TabPane>
        </Tabs>
      )}
    </div>
  );
};

export default Fournisseurs;

// Utils: export CSV
function exportDaysCsv(rows: ReportRow[], lang: "fr" | "en" | "kr") {
  const t = I18N[lang];
  const headers = t.csvDaysHeaders;
  const lines = rows.map((r) =>
    [
      dayjs(r.date).locale(lang).format("D MMM"),
      escapeCsv(r.retailerName),
      r.unitsSold,
      r.totalValue,
    ].join(";")
  );
  const totals = totalsFromRows(rows);
  const csv = [
    headers.join(";"),
    ...lines,
    [t.totals, "", totals.units, totals.value].join(";"),
  ].join("\n");
  triggerCsvDownload(csv, t.csvDaysFilename);
}

function exportProductsCsv(
  items: Array<{
    productId: string;
    title: string;
    quantity: number;
    barcode?: string;
    value: number;
  }>,
  lang: "fr" | "en" | "kr"
) {
  const t = I18N[lang];
  const headers = t.csvProductsHeaders;
  const lines = items.map((i) =>
    [escapeCsv(i.title), i.barcode || "", i.quantity, i.value].join(";")
  );
  const totals = totalsFromProducts(items);
  const csv = [
    headers.join(";"),
    ...lines,
    ["", "", "", ""].join(";"),
    [t.totals, "", totals.qty, totals.value].join(";"),
  ].join("\n");
  triggerCsvDownload(csv, t.csvProductsFilename);
}

function escapeCsv(s: string) {
  if (s.includes(";") || s.includes('"') || s.includes("\n")) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

function totalsFromRows(rows: ReportRow[]) {
  return rows.reduce(
    (acc, r) => {
      acc.units += r.unitsSold;
      acc.value += r.totalValue;
      return acc;
    },
    { units: 0, value: 0 }
  );
}

function totalsFromProducts(items: Array<{ quantity: number; value: number }>) {
  return items.reduce(
    (acc, i) => {
      acc.qty += i.quantity;
      acc.value += i.value;
      return acc;
    },
    { qty: 0, value: 0 }
  );
}

function triggerCsvDownload(csv: string, filename: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
